import { Filmomtale, Genre, ICustomImage } from '@filmweb/sanity-types';
import { CMSCroppedImage } from 'components/images/Image';
import SFacts from 'components/program/SFacts';
import SKinoklubbTicket from 'components/program/SKinoklubbTicket';
import SShowsContainer from 'components/program/SShowsContainer';
import STicketButton from 'components/program/STicketButton';
import isKinoklubb from 'lib/isKinoklubb';
import { toRem } from "lib/client/fonts";
import { IFilm } from 'lib/types/Program';
import { DocumentType_IMAGE, FwPakkeImageDocument, getFilmUrlFromEdi, IMovieArticle } from 'lib/types/sanity';
import Link from 'next/link';
import styled from 'styled-components';


//#region [Styles]
const SMovie = styled.article`
	margin: 0 10px 30px 10px;
	position: relative;

	@media screen and (min-width: 641px) {
		color: #fff;
		min-height: auto;
	}
`;

const SImageContainer = styled.div`
	width: 100%;
	display: block;
	height: auto;
	overflow: hidden;

	@media screen and (min-width: 641px) {
		aspect-ratio: 1420 / 382;
		position: absolute;
		z-index: -1;
		top: 0;
		left: 0;
		max-height: 100%;
		&::after {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			max-width: 1000px;
			height: 100%;
			background: linear-gradient(to right, #000 0%, transparent 80%);
		}

	}
`;

const SImage = styled(CMSCroppedImage)`
	width: 100%;
	display: block;
	height: auto;
`;

const SMovieTitle = styled.h1`
	font-size: 1.5rem;

	@media screen and (max-width: 640px) {
		width: 100%;
		margin: 0;
		position: absolute;
		top: 0;
		left: 0;
		padding: 10px;
		transform: translateY(-100%);
		color: white;
		background: linear-gradient(to bottom, transparent 0%,#000 70%);
	}

	@media screen and (min-width: 641px) {
		font-size: ${toRem(31)};
		flex: 1 1 auto !important;
	}

	a {
		color: currentColor;
	}
`;

const STextContainer = styled.div`
	padding: 0 10px 10px 10px;
	position: relative;

	@media screen and (min-width: 641px) {

		>* {
			flex: 0 0 auto;
		}

		padding: 25px;
		max-width: 1000px;

		display: flex;
		flex-direction: column;
		height: 100%;
	}


`;

//#endregion

//#region [Props]
type MovieWideProps = {
	movie: IFilm;
	fwPakkeMovie?: IMovieArticle;
	filmwebMovie?: Filmomtale;
	isLarge?: boolean;
	allowTitleOverride?: boolean;
};
//#endregion

//#region [Component]
export default function MovieWide({ movie, fwPakkeMovie, filmwebMovie, isLarge = false, allowTitleOverride }: MovieWideProps) {

	const image: FwPakkeImageDocument | ICustomImage | undefined = (fwPakkeMovie?.image as FwPakkeImageDocument) ?? filmwebMovie?.mainImageV2 ?? filmwebMovie?.imagesV2?.[0];

	let title: string = filmwebMovie?.title ?? movie?.title;
	if (allowTitleOverride && fwPakkeMovie?.titleOverride) {
		title = fwPakkeMovie.titleOverride;
	}

	return (<SMovie>
		{!filmwebMovie && <p>Mangler filmomtale fra Filmweb! ({movie.mainVersionId} - {movie.title})</p>}
		{filmwebMovie && <SImageContainer>
			{image && <SImage image={image as (ICustomImage | FwPakkeImageDocument)} width={isLarge ? 1440 : 640} heightRatio={isLarge ? (323 / 1200) : (9 / 16)} sizes="(min-width: 641px) 1420px, 640px" isFilmweb={image._type !== DocumentType_IMAGE} altText={`Bilde fra '${filmwebMovie.title}'`} />}
		</SImageContainer>}
		<STextContainer>
			<SMovieTitle><Link href={getFilmUrlFromEdi(filmwebMovie?.mainVersionEDI ?? movie.mainVersionId)}>{title}</Link></SMovieTitle>
			<SFacts>
				{(filmwebMovie?.genres?.length ?? 0) !== 0 && <li className="genre" title="Sjangre">{filmwebMovie?.genres?.map(g => (g as Genre).name).join(" / ")}</li>}
				{filmwebMovie?.runningTime && <li title="Spilletid">{filmwebMovie?.runningTime}</li>}
				{filmwebMovie?.ageRating?.age && <li title="Aldersgrense">{filmwebMovie?.ageRating?.age}</li>}
			</SFacts>
			<SShowsContainer>
				{movie.shows?.map(show => <STicketButton key={`${show.movieMainVersionId}|${show.showStart}`} show={show} showPassed appendShowType showDate />)}
				{filmwebMovie && isKinoklubb(filmwebMovie) && <SKinoklubbTicket />}
			</SShowsContainer>
		</STextContainer>
	</SMovie>);
}
//#endregion